import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { FormikHelpers } from 'formik';
import { useSearchParams } from 'react-router-dom';
import DeleteAction from '../../../common/components/actions/delete-action-button/DeleteAction';
import MutationResultHandler from '../../../common/components/handlers/MutationResultHandler';
import QueryResultHandler from '../../../common/components/handlers/QueryResultHandler';
import ConfirmationModal from '../../../common/components/modals/ConfirmationModal';
import { notify } from '../../../common/utils/notification';
import { PAGE_KEY } from '../api';
import { useDeleteActionUrl } from '../api/useDeleteActionUrl';

import { useEditActionUrl } from '../api/useEditActionUrl';
import { useGetActionUrlById } from '../api/useGetActionUrlById';
import { useGetActionUrls } from '../api/useGetActionUrls';
import { hasTriggerConflict } from '../utils/validateTriggerConflict';
import { ActionUrlForm } from './action-url-form/ActionUrlForm';
import { IActionUrlForm } from '../types/ActionUrl';

import cardStyles from '../../../../theme/styles/Card.module.css';

interface EditActionUrlFromProps {
  id: string;
}

export const EditActionUrlFrom: FC<EditActionUrlFromProps> = ({ id }) => {
  const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] = useState(false);
  const { isSuccess, data: actionUrlData } = useGetActionUrls();

  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const getApiResult = useGetActionUrlById(id);
  const editApiResult = useEditActionUrl(id);
  const deleteApiResult = useDeleteActionUrl();

  const submit = (data: IActionUrlForm, helpers: FormikHelpers<IActionUrlForm>) => {
    if (!actionUrlData?.items || !isSuccess) {
      return;
    }

    const hasConflict = hasTriggerConflict(data, actionUrlData.items, {
      excludeId: id,
    });

    if (hasConflict) {
      return notify.error(t('common:actionUrlDuplicateError'), {
        duration: 6000,
        position: 'bottom-right',
        style: {
          marginRight: '8px'
        },
      });
    }

    editApiResult.mutate(data, {
      onSuccess: () => {
        void queryClient.invalidateQueries([`${PAGE_KEY}/getAll`]);
        void queryClient.invalidateQueries([`${PAGE_KEY}/get`]);
        helpers.resetForm();
      }
    })
  };

  const handleDeleteActionUrl = useCallback(() => {
    deleteApiResult.mutate(id, {
      onSuccess: () => {
        setSearchParams('');
        void queryClient.invalidateQueries([`${PAGE_KEY}/getAll`]);
      },
    });
  }, [id, deleteApiResult, setSearchParams, queryClient]);

  const DeleteBtn = !editApiResult.isLoading && !deleteApiResult.isLoading ? (
    <DeleteAction
      label={t('common:delete')}
      onClick={() => setIsDeleteConfirmationVisible(true)}
    />
  ) : null;

  return (
    <div>
      <QueryResultHandler result={getApiResult}>
        {({ data }) => (
          <>
            <div className={cardStyles.card_content} style={{ overflow: 'hidden' }}>
              <ActionUrlForm
                initialState={data}
                disabledActions={getApiResult.isLoading}
                onSubmit={submit}
                deleteBtn={DeleteBtn}
              />
              <MutationResultHandler result={editApiResult} />
            </div>
            {isDeleteConfirmationVisible && (
              <ConfirmationModal
                confirmBtnText={t('common:delete')}
                onCancel={() => setIsDeleteConfirmationVisible(false)}
                onSuccess={() => {
                  handleDeleteActionUrl();
                  setIsDeleteConfirmationVisible(false);
                }}
                dialogContentProps={{
                  title: t('common:deleteActionUrl'),
                  subText: `${t('common:deleteActionUrlQuestion')} "${data.title}"?`,
                }}
              />
            )}
          </>
        )}
      </QueryResultHandler>
    </div>
  );
};
